import type { ReactElement, ReactNode } from 'react';
import { useState } from 'react';
import type { CompositionSlide } from '../../../../src/composition/types.js';
import type { Proposal, DocumentSection } from '@shared/types';

/**
 * Internal debug panel (spec §53) — engine/version, the CompositionPlan
 * slide this section was rendered from, DesignDNA, DesignSpec, SceneGraph,
 * resolved box dims, critique and repairs. Read-only, collapsible, never
 * styled for a customer (fixed dev chrome, not `.tpl-momentum-v2` tokens).
 * Rendered only when the caller has already gated on `readOnly === false`
 * and `?debug=studio` (see `StudioPage`) — this component itself does no
 * gating so it stays trivially testable.
 */
export function StudioDebugPanel({ proposal, section }: { proposal: Proposal; section: DocumentSection }): ReactElement | null {
  const [open, setOpen] = useState(true);
  const studio = section.studio;
  if (!studio) return null;

  const compSlide: CompositionSlide | undefined = section.compositionSlideId
    ? proposal.composition?.plan.sections
        .flatMap((s) => s.slides)
        .find((slide) => slide.id === section.compositionSlideId)
    : undefined;

  const generation = proposal.document?.generation;
  const designDNA = proposal.document?.designDirection?.dna;

  return (
    <div className="tpl-studio-debug" data-open={open}>
      <button type="button" className="tpl-studio-debug-toggle" onClick={() => setOpen((v) => !v)}>
        Studio debug {open ? '▾' : '▸'} — {section.id}
      </button>
      {open ? (
        <div className="tpl-studio-debug-body">
          <Section title="Engine">
            <Kv label="engine" value={generation?.engine} />
            <Kv label="engineVersion" value={generation?.engineVersion} />
            <Kv label="compositionPlanVersion" value={generation?.compositionPlanVersion} />
          </Section>

          {compSlide ? (
            <Section title="Composition slide">
              <Kv label="purpose" value={compSlide.purpose} />
              <Kv label="reasonForSlide" value={compSlide.reasonForSlide} />
              <Kv label="slideType" value={compSlide.slideType} />
              <Kv label="density" value={compSlide.density} />
            </Section>
          ) : (
            <Section title="Composition slide">
              <div className="tpl-studio-debug-empty">No composition slide for compositionSlideId={section.compositionSlideId ?? '(none)'}</div>
            </Section>
          )}

          {designDNA ? (
            <Section title="DesignDNA">
              <Json value={designDNA} />
            </Section>
          ) : null}

          <Section title="DesignSpec">
            <Json value={studio.designSpec} />
          </Section>

          <Section title="SceneGraph">
            <Json value={studio.sceneGraph} />
          </Section>

          <Section title="Resolved box dims">
            <Kv label="frame" value={JSON.stringify(studio.resolved.frame)} />
            <Kv label="root box" value={JSON.stringify(studio.resolved.root.box)} />
            <Kv label="warnings" value={studio.resolved.warnings.join('; ') || '(none)'} />
          </Section>

          {studio.critique ? (
            <Section title="Critique">
              <Json value={studio.critique} />
            </Section>
          ) : null}

          {studio.repairs?.length ? (
            <Section title={`Repairs (${studio.repairs.length})`}>
              <Json value={studio.repairs} />
            </Section>
          ) : null}

          {studio.validationAttempts?.length ? (
            <Section title={`Validation repair attempts (${studio.validationAttempts.length})`}>
              <Json value={studio.validationAttempts} />
            </Section>
          ) : null}

          {studio.fallback ? (
            <Section title="Fallback">
              <Kv label="to" value={studio.fallback.to} />
              <Kv label="reason" value={studio.fallback.reason} />
            </Section>
          ) : null}

          {studio.readiness ? (
            <Section title="Content readiness">
              <Kv label="readiness" value={studio.readiness} />
              {studio.hydrated ? (
                <>
                  <Kv label="headline" value={studio.hydrated.headline} />
                  <Kv label="tactics" value={studio.hydrated.tactics.map((t) => t.productId).join(', ')} />
                  <Kv label="channels" value={studio.hydrated.channels.map((c) => c.productId).join(', ')} />
                  <Kv label="relationships" value={studio.hydrated.relationships.length} />
                  <Kv label="unresolvedRefs" value={studio.hydrated.unresolvedRefs.join(', ')} />
                  <Kv label="sourceRefs" value={studio.hydrated.sourceRefs.join(', ')} />
                </>
              ) : null}
            </Section>
          ) : null}

          {studio.contentDiagnostics?.length ? (
            <Section title={`Content diagnostics (${studio.contentDiagnostics.length})`}>
              <Json value={studio.contentDiagnostics} />
            </Section>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): ReactElement {
  return (
    <div className="tpl-studio-debug-section">
      <div className="tpl-studio-debug-section-title">{title}</div>
      {children}
    </div>
  );
}

function Kv({ label, value }: { label: string; value: unknown }): ReactElement {
  return (
    <div className="tpl-studio-debug-kv">
      <span className="tpl-studio-debug-key">{label}</span>
      <span className="tpl-studio-debug-value">{value === undefined || value === null || value === '' ? '(none)' : String(value)}</span>
    </div>
  );
}

function Json({ value }: { value: unknown }): ReactElement {
  return <pre className="tpl-studio-debug-json">{JSON.stringify(value, null, 2)}</pre>;
}

/** `?debug=studio` gate, read once per call — cheap enough to call per render. */
export function isStudioDebugRequested(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('debug') === 'studio';
}
